<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[520px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-md tw-my-10 tw-rounded">
            <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-text-gray-600 tw-bg-gray-50">
                {{ title }}
            </div>

            <div class="tw-p-5 tw-bg-white tw-space-y-3">
                <p class="tw-text-sm tw-text-gray-500">{{ description }}</p>
                <div v-if="showPassword">
                    <FormField left-icon="solar:lock-keyhole-bold-duotone" v-model="password"
                        :label="$t('portal.customers.set-password')" type="password" :error="err('password')"
                        :help="mode === 'password' ? '' : $t('portal.customers.password-optional')" />
                </div>
            </div>

            <div class="tw-p-4 tw-border-t tw-justify-end tw-flex tw-items-center tw-gap-2 tw-bg-gray-50">
                <a-button :disabled="loading" @click="onCancel" intent="simple" class="tw-min-w-[110px]">{{ $t('buttons.cancel') }}</a-button>
                <a-button :disabled="loading" :loading="loading" :intent="confirmIntent" @click="onConfirm" class="tw-min-w-[110px]">{{ confirmLabel }}</a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import { portalAccess } from '@/api/customers';
import useAlert from '@/composables/useAlert';
import { useValidation } from '@/composables/useValidation';

const props = defineProps({
    visible: { type: Boolean, default: false },
    customerId: { type: [Number, String], default: null },
    // 'enable' | 'disable' | 'password'
    mode: { type: String, default: 'enable' },
});
const emit = defineEmits(['cancel', 'saved']);
const i18n = useI18n();

const loading = ref(false);
const password = ref('');
const errors = ref({});
const err = useValidation(errors);

const showPassword = computed(() => props.mode !== 'disable');

const title = computed(() => ({
    enable: i18n.t('portal.customers.enable-portal'),
    disable: i18n.t('portal.customers.disable-portal'),
    password: i18n.t('portal.customers.edit-password'),
}[props.mode]));

const description = computed(() => ({
    enable: i18n.t('portal.customers.enable-portal-desc'),
    disable: i18n.t('portal.customers.disable-portal-desc'),
    password: i18n.t('portal.customers.edit-password-desc'),
}[props.mode]));

const confirmLabel = computed(() => ({
    enable: i18n.t('portal.customers.enable'),
    disable: i18n.t('portal.customers.disable'),
    password: i18n.t('buttons.update'),
}[props.mode]));

const confirmIntent = computed(() => (props.mode === 'disable' ? 'danger' : 'primary'));

watch(() => props.visible, (v) => {
    if (v) { password.value = ''; errors.value = {}; }
});

const onCancel = () => { if (!loading.value) emit('cancel'); };

const onConfirm = async () => {
    // Editing the password requires a value.
    if (props.mode === 'password' && !password.value) {
        errors.value = { password: 'required' };
        return;
    }

    loading.value = true;
    errors.value = {};
    try {
        let payload;
        if (props.mode === 'disable') {
            payload = { enabled: false };
        } else {
            // enable or password: keep access on, set password only when provided.
            payload = { enabled: true, ...(password.value ? { password: password.value } : {}) };
        }

        const { data } = await portalAccess(props.customerId, payload);
        if (data.code === 'SUCCESS') {
            const alertKey = {
                enable: 'portal.customers.portal-enabled',
                disable: 'portal.customers.portal-disabled',
                password: 'portal.customers.password-updated',
            }[props.mode];
            useAlert(i18n.t(alertKey));
            emit('saved', data.customer);
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) errors.value[k] = e.response.data.errors[k][0];
        } else if (e.response?.data?.message) {
            useAlert(e.response.data.message, 'danger');
        }
    } finally {
        loading.value = false;
    }
};
</script>
